import * as Haptics from 'expo-haptics';
import React, { useState } from 'react';
import { Modal, Pressable, ScrollView, View } from 'react-native';
import { KeyboardController } from 'react-native-keyboard-controller';
import { Ionicons } from '@expo/vector-icons';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { cn } from '@/utils/cn';

export interface FilterSelectOption<T extends string> {
  value: T;
  label: string;
  /** Explica o recorte em uma linha (ex.: "8 a 14 de ago"). */
  hint?: string;
}

export interface FilterSelectProps<T extends string> {
  options: FilterSelectOption<T>[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  icon?: React.ComponentProps<typeof Ionicons>['name'];
  /** Linha de apoio no controle fechado (ex.: "8 a 14 de ago · 3 agendamentos"). */
  summary?: string;
  /** Título da folha aberta. Sem ele, usa o rótulo. */
  sheetTitle?: string;
  className?: string;
}

/**
 * Filtro como campo, não como parede de botões: uma linha diz o que está
 * filtrado e o que isso significa; tocar abre a lista completa, onde cada opção
 * mostra o recorte que ela representa. Nada fica escondido atrás de rolagem
 * lateral, e a tela não é dominada por pílulas.
 */
export function FilterSelect<T extends string>({
  options,
  value,
  onChange,
  label,
  icon = 'calendar-outline',
  summary,
  sheetTitle,
  className,
}: FilterSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.value === value);

  const pick = (next: T) => {
    if (process.env.EXPO_OS === 'ios') void Haptics.selectionAsync();
    onChange(next);
    setOpen(false);
  };

  return (
    <>
      <PressableScale
        haptic
        onPress={() => void KeyboardController.dismiss().then(() => setOpen(true))}
        accessibilityRole="button"
        accessibilityLabel={`${label}: ${selected?.label ?? 'selecionar'}`}
        className={cn(
          'flex-row items-center gap-3 rounded-2xl border border-neutral-200 bg-card px-4 py-3',
          className,
        )}
        style={{ borderCurve: 'continuous' }}
      >
        <View className="h-9 w-9 items-center justify-center rounded-full bg-primary/10 dark:bg-primary">
          <Ionicons className="text-primary dark:text-white" name={icon} size={17} />
        </View>

        <View className="min-w-0 flex-1">
          <ThemedText className="text-xs text-neutral-500">{label}</ThemedText>
          <ThemedText className="font-semibold text-base text-neutral-900">
            {selected?.label ?? 'Selecionar'}
          </ThemedText>
          {summary ? <ThemedText className="text-xs text-neutral-400">{summary}</ThemedText> : null}
        </View>

        <Ionicons className="text-neutral-400" name="chevron-down" size={18} />
      </PressableScale>

      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable className="flex-1 justify-end bg-black/40" onPress={() => setOpen(false)}>
          <Pressable
            onPress={(event) => event.stopPropagation()}
            className="max-h-[75%] rounded-t-3xl bg-card pb-8 pt-2"
            style={{ borderCurve: 'continuous' }}
          >
            <View className="items-center py-2">
              <View className="h-1 w-10 rounded-full bg-neutral-200" />
            </View>

            <View className="flex-row items-center justify-between px-5 pb-2 pt-1">
              <ThemedText className="font-bold text-base text-neutral-900">
                {sheetTitle ?? label}
              </ThemedText>
              <Pressable onPress={() => setOpen(false)} hitSlop={10} accessibilityLabel="Fechar">
                <Ionicons className="text-neutral-500" name="close" size={20} />
              </Pressable>
            </View>

            <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
              <View className="gap-1 px-3 pb-2">
                {options.map((option) => {
                  const isSelected = option.value === value;
                  return (
                    <Pressable
                      key={option.value}
                      onPress={() => pick(option.value)}
                      accessibilityRole="button"
                      accessibilityState={{ selected: isSelected }}
                      className={cn(
                        'flex-row items-center gap-3 rounded-2xl px-3 py-3 active:opacity-70',
                        isSelected && 'bg-primary/5',
                      )}
                      style={{ borderCurve: 'continuous' }}
                    >
                      <View className="min-w-0 flex-1">
                        <ThemedText
                          className={cn(
                            'text-base',
                            isSelected ? 'font-bold text-primary' : 'font-medium text-neutral-900',
                          )}
                        >
                          {option.label}
                        </ThemedText>
                        {option.hint ? (
                          <ThemedText className="text-xs text-neutral-500">
                            {option.hint}
                          </ThemedText>
                        ) : null}
                      </View>

                      {isSelected ? (
                        <Ionicons name="checkmark-circle" size={20} color="#FD5001" />
                      ) : null}
                    </Pressable>
                  );
                })}
              </View>
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}
